import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { withStyles } from "@mui/styles";
import { List, ListItem, ListItemText, ListItemIcon, Collapse, IconButton, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, ChatBubble, Share } from "@mui/icons-material";
import { Alert } from "orion-components/CBComponents/Icons";
import { TargetingIcon } from "orion-components/SharedComponents";
import { Translate, getTranslation } from "orion-components/i18n";

const styles = {
	root: {
		color: "#fff",
		backgroundColor: "#494D53",
		"&:hover": {
			backgroundColor: "#494D53"
		},
		borderRadius: 5,
		paddingLeft: 12,
		paddingRight: 12,
		minHeight: 70
	},
	selected: {
		backgroundColor: "#494D53",
		borderRight: "3px solid #35b7f3"
	},
	container: {
		paddingLeft: 30
	},
	nested: {
		marginBottom: 8,
		paddingLeft: 60
	},
	containerRTL: {
		paddingRight: 30
	}
};

const propTypes = {
	classes: PropTypes.object.isRequired,
	primaryText: PropTypes.string.isRequired,
	secondaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	selected: PropTypes.bool,
	childSelected: PropTypes.bool,
	children: PropTypes.array,
	alerts: PropTypes.bool,
	icon: PropTypes.element,
	handleSelect: PropTypes.func,
	commentCount: PropTypes.number,
	filterButton: PropTypes.element,
	open: PropTypes.bool,
	dir: PropTypes.string,
	filterType: PropTypes.string,
	geometry: PropTypes.object,
	feedId: PropTypes.string,
	id: PropTypes.string,
	sharedBy: PropTypes.string,
	targetingEnabled: PropTypes.bool,
	iconStyles: PropTypes.object,
	minimized: PropTypes.bool,
	endAdornment: PropTypes.element,
	customStyles: PropTypes.object,
	cardHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const defaultProps = {
	secondaryText: "",
	selected: false,
	childSelected: false,
	children: [],
	alerts: false,
	filtered: false,
	handleFilter: null,
	icon: null,
	handleSelect: null,
	commentCount: 0,
	filterButton: null,
	targetingEnabled: true,
	minimized: false,
	endAdornment: null,
	customStyles: {},
	cardStyles: {}
};

const Collection = ({
	open,
	classes,
	primaryText,
	secondaryText,
	selected,
	childSelected,
	children,
	alerts,
	filterButton,
	icon,
	handleSelect,
	commentCount,
	dir,
	filterType,
	geometry,
	feedId,
	id,
	sharedBy,
	targetingEnabled,
	iconStyles,
	handleCollectionExpand,
	minimized,
	endAdornment,
	customStyles,
	cardStyles
}) => {
	const [stateOpen, setStateOpen] = useState(open ? true : false);

	const handleExpand = e => {
		e.preventDefault();
		e.stopPropagation();
		setStateOpen(!stateOpen);
		if (handleCollectionExpand) handleCollectionExpand({ id, expand: !stateOpen });
	};
	const styles = {
		primary: {
			color: "#fff"
		},
		secondary: {
			color: "#B5B9BE",
			...(minimized && { fontSize: 10, lineHeight: "8px", marginTop: 8 })
		},
		icon: {
			...(dir === "ltr" && { marginRight: 0 }),
			...(dir === "rtl" && { marginLeft: 0 })
		},
		commentIcon: {
			...(dir === "ltr" && { marginRight: 0 }),
			...(dir === "rtl" && { marginLeft: 0 }),
			minWidth: "35px"
		},
		layered: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: "35px"
		},
		selected: {
			...(minimized && { minHeight: 40 }),
			backgroundColor: "#383D48"
		},
		childSelected: {
			borderWidth: dir === "rtl" ? "0 0 0 3px" : "0 3px 0 0",
			borderColor: "#383D48",
			borderStyle: "solid"
		},
		textRightAlign: {
			...((minimized || sharedBy) && { margin: 0 }),
			...(dir === "rtl" && { textAlign: "right", paddingLeft: 5 }),
			...(dir === "ltr" && { paddingRight: 5 })
		},
		targetingIcon: {
			...(dir === "ltr" && { paddingLeft: 0 }),
			...(dir === "rtl" && { paddingRight: 0 })
		},
		shareIcon: {
			fontSize: 14,
			color: "rgb(181, 185, 190)",
			...(dir === "ltr" && { marginRight: 5 }),
			...(dir === "rtl" && { marginLeft: 5 })
		}
	};

	const getSecondaryText = () => {
		if (sharedBy) {
			return (
				<>
					{secondaryText}
					<span style={{ display: "flex" }}>
						<Share style={styles.shareIcon} />
						{`${getTranslation("global.CBComponents.CBCollection.sharedBy")} ${sharedBy}`}
					</span>
				</>
			);
		} else {
			return secondaryText || null;
		}
	};

	return (
		<div style={{ marginBottom: minimized ? 0 : 12, ...customStyles }}>
			<ListItem
				button={Boolean(handleSelect)}
				onClick={handleSelect ? handleSelect : null}
				style={
					selected
						? styles.selected
						: childSelected
							? styles.childSelected
							: minimized
								? { minHeight: 40, padding: 8 }
								: {}
				}
				className={classNames(classes.root)}
				sx={cardStyles}
			>
				{!filterType && children.length > 0 && (
					<ListItemIcon style={{ minWidth: "auto" }}>
						{stateOpen ? (
							<IconButton onClick={handleExpand}>
								<ExpandLess />
							</IconButton>
						) : (
							<IconButton onClick={handleExpand}>
								<ExpandMore />
							</IconButton>
						)}
					</ListItemIcon>
				)}
				{filterType && targetingEnabled && ((id && feedId) || geometry) && (
					<TargetingIcon
						feedId={feedId}
						id={id}
						geometry={geometry}
						iconContainerStyle={styles.targetingIcon}
					/>
				)}

				{children.length > 0 && Boolean(filterButton) && <ListItemIcon>{filterButton}</ListItemIcon>}
				{icon && (
					<ListItemIcon style={iconStyles || styles.icon}>
						{icon}
						{filterType && (
							<Translate
								value={`global.CBComponents.CBCollection.${filterType}`}
								style={{ fontSize: 9, marginTop: 5 }}
							/>
						)}
					</ListItemIcon>
				)}
				<ListItemText
					primary={primaryText}
					secondary={getSecondaryText()}
					primaryTypographyProps={{
						style: minimized ? { fontSize: 12, lineHeight: "10px" } : {},
						noWrap: true,
						variant: "body1",
						component: "p"
					}}
					secondaryTypographyProps={{
						style: styles.secondary,
						noWrap: false,
						variant: "body2"
					}}
					style={styles.textRightAlign}
				/>
				{alerts && <Alert fontSize="large" />}
				{Boolean(commentCount) && (
					<div style={styles.layered}>
						<ListItemIcon style={styles.commentIcon}>
							<ChatBubble style={styles.secondary} fontSize="large" />
						</ListItemIcon>
						<Typography
							style={{
								position: "absolute",
								bottom: 26,
								color: "#000"
							}}
						>
							{commentCount < 1000 ? commentCount : "1k+"}
						</Typography>
					</div>
				)}
				{endAdornment && (
					<ListItemIcon style={{ ...styles.icon, minWidth: "unset", height: 24 }}>
						{endAdornment}
					</ListItemIcon>
				)}
			</ListItem>
			<Collapse
				className={dir && dir == "rtl" ? classes.containerRTL : classes.container}
				in={stateOpen}
				timeout="auto"
				unmountOnExit
			>
				<List>{children}</List>
			</Collapse>
		</div>
	);
};

Collection.propTypes = propTypes;
Collection.defaultProps = defaultProps;

export default withStyles(styles)(Collection);
